import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, CheckCircle, XCircle, Loader2 } from "lucide-react";
import {AutoTranslate} from "@/lib/autoTranslate.tsx";
import { useTranslation} from "react-i18next";
import { clearFormDraft, readFormDraft, writeFormDraft } from "@/lib/formDraftStorage";

interface JobDescriptionInputProps {
  onSubmit: (jobDescription: string) => void;
  onSkip: () => void;
  isLoading?: boolean;
}

export function JobDescriptionInput({ onSubmit, onSkip, isLoading = false }: JobDescriptionInputProps) {
  const transText = useTranslation().t('autoTranslate', { returnObjects: true });
  const { i18n } = useTranslation();

  const [jobDescription, setJobDescription] = useState(() => (
    readFormDraft('jobDescription', "")
  ));

  useEffect(() => {
    writeFormDraft('jobDescription', jobDescription);
  }, [jobDescription]);

  const handleSubmit = () => {
    if (jobDescription.trim()) {
      clearFormDraft('jobDescription');
      onSubmit(jobDescription.trim());
    }
  };

  const handleSkip = () => {
    clearFormDraft('jobDescription');
    onSkip();
  };

  return (
    <Card className="bg-card/90 backdrop-blur-sm border-card-border mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Briefcase className="h-5 w-5 text-primary" />
          {AutoTranslate(`Tailor Your Resume to a Job`)}
        </CardTitle>
        <CardDescription>
          {AutoTranslate(`Paste a job description and we'll analyze how well your resume matches, suggest improvements, and identify skill gaps`)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          placeholder={AutoTranslate(`Paste the job description here...

Example:
We are looking for a Senior Software Engineer with 5+ years of experience in React, TypeScript, and Node.js. The ideal candidate should have experience with cloud platforms (AWS/GCP), CI/CD pipelines, and agile methodologies...`)}
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          className="min-h-[200px] resize-none"
          disabled={isLoading}
        />

        <div className="flex gap-3">
          <Button
            onClick={handleSubmit}
            disabled={!jobDescription.trim() || isLoading}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {AutoTranslate(`Analyzing...`)}
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {AutoTranslate(`Yes, Analyze & Tailor`)}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isLoading}
            className="flex-1"
          >
            <XCircle className="h-4 w-4 mr-2" />
            {AutoTranslate(`No, Skip This`)}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          {AutoTranslate(`This feature analyzes keyword matches, identifies skill gaps, and provides an ATS compatibility score`)}
        </p>
      </CardContent>
    </Card>
  );
}
